<!--
  Copyright (c) 2025 NoorWays. All rights reserved.
  Licensed under the terms specified in the LICENSE.txt file.
-->

<template>
  <div class="home">
    <!-- Premium User Welcome Section -->
    <header v-if="isAuthenticated && isPremium" class="hero hero-premium">
      <!-- Animated Background Elements -->
      <div class="hero-background">
        <div class="hero-pattern"></div>
        <div class="hero-orb hero-orb-1"></div>
        <div class="hero-orb hero-orb-2"></div>
        <div class="hero-orb hero-orb-3"></div>
      </div>
      
      <div class="container hero-content">
        <!-- Premium Badge -->
        <div class="hero-badge premium-badge">
          <span class="badge-pulse"></span>
          <span class="badge-text">✨ Premium</span>
        </div>
        
        <h1 class="hero-title">{{ $t('home.welcome.premium.title', { name: user?.name || 'Premium User' }) }}</h1>
        <p class="hero-subtitle">{{ $t('home.welcome.premium.subtitle') }}</p>
        
        <div class="hero-actions">
          <router-link to="/editor" class="btn btn-primary btn-large">
            <span>{{ $t('navigation.editor') }}</span>
            <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
          <router-link to="/settings" class="btn btn-secondary">
            {{ $t('navigation.settings') }}
          </router-link>
        </div>
        
        <!-- Premium Status Badges -->
        <div class="trust-badges">
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t('subscription.features.unlimitedRequests') }}</span>
          </div>
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t('subscription.features.advancedFeatures') }}</span>
          </div>
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
            </svg>
            <span>{{ $t('subscription.features.prioritySupport') }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Free User Welcome Section -->
    <header v-else-if="isAuthenticated && !isPremium" class="hero hero-free">
      <!-- Animated Background Elements -->
      <div class="hero-background">
        <div class="hero-pattern"></div>
        <div class="hero-orb hero-orb-1"></div>
        <div class="hero-orb hero-orb-2"></div>
        <div class="hero-orb hero-orb-3"></div>
      </div>
      
      <div class="container hero-content">
        <!-- Free Badge -->
        <div class="hero-badge free-badge">
          <span class="badge-pulse"></span>
          <span class="badge-text">{{ $t('subscription.free') }}</span>
        </div>
        
        <h1 class="hero-title">{{ $t('home.welcome.free.title') }}</h1>
        <p class="hero-subtitle">{{ $t('home.welcome.free.subtitle') }}</p>
        
        <!-- Usage Progress Card -->
        <div class="usage-card" v-if="user">
          <div class="usage-header">
            <span class="usage-label">{{ $t('home.welcome.free.requestsUsed') }}</span>
            <span class="usage-count">{{ subscriptionStore.usageCount }} / {{ subscriptionStore.maxFreeUsage }}</span>
          </div>
          <div class="usage-progress">
            <div 
              class="usage-progress-bar" 
              :style="{ width: `${(subscriptionStore.usageCount / subscriptionStore.maxFreeUsage) * 100}%` }"
              :class="{ 'usage-warning': subscriptionStore.usageCount >= subscriptionStore.maxFreeUsage * 0.8 }"
            ></div>
          </div>
          <router-link
            to="/pricing"
            class="upgrade-link"
            v-if="subscriptionStore.usageCount >= subscriptionStore.maxFreeUsage * 0.5"
          >
            {{ $t('home.welcome.free.upgradeNow') }} →
          </router-link>
        </div>
        
        <div class="hero-actions">
          <router-link to="/editor" class="btn btn-primary btn-large">
            <span>{{ $t('navigation.editor') }}</span>
            <svg class="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </router-link>
          <router-link to="/pricing" class="btn btn-secondary">
            {{ $t('home.welcome.free.upgradeToPremium') }}
          </router-link>
        </div>
        
        <!-- Free Features Badges -->
        <div class="trust-badges">
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t('subscription.features.fiveAiRequests') }}</span>
          </div>
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t('subscription.features.grammarChecking') }}</span>
          </div>
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"/>
            </svg>
            <span>{{ $t('home.trust.secure', 'Secure & Private') }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Default Marketing Hero for Non-Authenticated Users -->
    <header v-else class="hero hero-marketing">
      <!-- Animated Background Elements -->
      <div class="hero-background">
        <div class="hero-pattern"></div>
        <div class="hero-orb hero-orb-1"></div>
        <div class="hero-orb hero-orb-2"></div>
        <div class="hero-orb hero-orb-3"></div>
      </div>
      
      <div class="container hero-content">
        <!-- Badge -->
        <div class="hero-badge">
          <span class="badge-pulse"></span>
          <span class="badge-text">{{ $t('home.badge', 'AI-Powered Writing') }}</span>
        </div>
        
        <h1 class="hero-title">{{ $t('app.title') }}</h1>
        <p class="hero-subtitle">{{ $t('app.description') }}</p>
        
        <div class="hero-actions">
          <router-link to="/auth/signup" class="btn btn-primary btn-large">
            <svg class="btn-icon btn-icon-left" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
            </svg>
            <span>{{ $t('home.hero.startFree') }}</span>
          </router-link>
          <a href="#features" class="btn btn-secondary" @click.prevent="scrollToFeatures">
            {{ $t('home.hero.seeHowItWorks') }}
          </a>
        </div>
        <p class="hero-microcopy">{{ $t('home.hero.noCreditCard') }}</p>
        
        <!-- Trust Badges -->
        <div class="trust-badges">
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t('home.trust.freeToStart', 'Free to Start') }}</span>
          </div>
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2H7V7a3 3 0 015.905-.75 1 1 0 001.937-.5A5.002 5.002 0 0010 2z"/>
            </svg>
            <span>{{ $t('home.trust.secure', 'Secure & Private') }}</span>
          </div>
          <div class="trust-badge">
            <svg class="trust-icon" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd"/>
            </svg>
            <span>{{ $t('home.trust.instant', 'Instant Results') }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Features Section - Different content for premium vs non-premium users -->
    <section v-if="isPremium" class="features premium-features">
      <div class="container">
        <h2>{{ $t('home.premium.featuresTitle') }}</h2>
        <div class="features-grid">
          <div class="feature-card premium-card">
            <h3>{{ $t('editor.improve') }}</h3>
            <p>{{ $t('home.premium.features.improve') }}</p>
          </div>
          <div class="feature-card premium-card">
            <h3>{{ $t('editor.rewrite') }}</h3>
            <p>{{ $t('home.premium.features.rewrite') }}</p>
          </div>
          <div class="feature-card premium-card">
            <h3>{{ $t('editor.checkGrammar') }}</h3>
            <p>{{ $t('home.premium.features.grammar') }}</p>
          </div>
          <div class="feature-card premium-card">
            <h3>{{ $t('editor.translate') }}</h3>
            <p>{{ $t('home.premium.features.translate') }}</p>
          </div>
          <div class="feature-card premium-card">
            <h3>{{ $t('editor.checkPlagiarism') }}</h3>
            <p>{{ $t('home.premium.features.plagiarism') }}</p>
          </div>
          <div class="feature-card premium-card">
            <h3>{{ $t('editor.detectAI') }}</h3>
            <p>{{ $t('home.premium.features.aiDetection') }}</p>
          </div>
          <div class="feature-card premium-card">
            <h3>{{ $t('editor.checkCitations') }}</h3>
            <p>{{ $t('home.premium.features.citations') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section - Only for non-authenticated users -->
    <section v-if="!isAuthenticated" id="features" class="seo-features">
      <div class="container">
        <h2>{{ $t('home.seoFeatures.title') }}</h2>
        <p>{{ $t('home.seoFeatures.description') }}</p>
        <div class="seo-features-grid">
          <div class="seo-feature-card">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <svg class="feature-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z"/>
              </svg>
            </div>
            <h3>{{ $t('home.seoFeatures.grammar.title') }}</h3>
            <p>{{ $t('home.seoFeatures.grammar.description') }}</p>
            <router-link to="/grammar-checker" class="feature-link">
              <span>{{ $t('home.seoFeatures.grammar.link') }}</span>
              <svg class="link-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </router-link>
          </div>
          <div class="seo-feature-card">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <svg class="feature-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"/>
              </svg>
            </div>
            <h3>{{ $t('home.seoFeatures.writing.title') }}</h3>
            <p>{{ $t('home.seoFeatures.writing.description') }}</p>
            <router-link to="/ai-writing-assistant" class="feature-link">
              <span>{{ $t('home.seoFeatures.writing.link') }}</span>
              <svg class="link-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </router-link>
          </div>
          <div class="seo-feature-card">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <svg class="feature-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z"/>
              </svg>
            </div>
            <h3>{{ $t('home.seoFeatures.spelling.title') }}</h3>
            <p>{{ $t('home.seoFeatures.spelling.description') }}</p>
            <router-link to="/spell-checker" class="feature-link">
              <span>{{ $t('home.seoFeatures.spelling.link') }}</span>
              <svg class="link-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </router-link>
          </div>
          <div class="seo-feature-card">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <svg class="feature-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 3.75H6A2.25 2.25 0 003.75 6v1.5M16.5 3.75H18A2.25 2.25 0 0120.25 6v1.5m0 9V18A2.25 2.25 0 0118 20.25h-1.5m-9 0H6A2.25 2.25 0 013.75 18v-1.5M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <h3>{{ $t('home.seoFeatures.plagiarism.title') }}</h3>
            <p>{{ $t('home.seoFeatures.plagiarism.description') }}</p>
            <router-link to="/plagiarism-checker" class="feature-link">
              <span>{{ $t('home.seoFeatures.plagiarism.link') }}</span>
              <svg class="link-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </router-link>
          </div>
          <div class="seo-feature-card">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <svg class="feature-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z"/>
              </svg>
            </div>
            <h3>{{ $t('home.seoFeatures.aiDetection.title') }}</h3>
            <p>{{ $t('home.seoFeatures.aiDetection.description') }}</p>
            <router-link to="/ai-content-detector" class="feature-link">
              <span>{{ $t('home.seoFeatures.aiDetection.link') }}</span>
              <svg class="link-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </router-link>
          </div>
          <div class="seo-feature-card">
            <div class="feature-icon-wrapper">
              <div class="feature-icon-bg"></div>
              <svg class="feature-icon-svg" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"/>
              </svg>
            </div>
            <h3>{{ $t('home.seoFeatures.citations.title') }}</h3>
            <p>{{ $t('home.seoFeatures.citations.description') }}</p>
            <router-link to="/citation-checker" class="feature-link">
              <span>{{ $t('home.seoFeatures.citations.link') }}</span>
              <svg class="link-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section - Hidden for all authenticated users -->
    <section v-if="!isAuthenticated" class="cta-section">
      <div class="container">
        <h2>{{ $t('home.cta.title') }}</h2>
        <p>{{ $t('home.cta.description') }}</p>
        <div class="cta-actions">
          <router-link to="/auth/signup" class="btn btn-primary btn-large">
            {{ $t('auth.signUp') }} - {{ $t('home.cta.signUpFree') }}
          </router-link>
          <router-link to="/pricing" class="btn btn-outline">
            {{ $t('navigation.pricing') }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useSubscriptionStore } from '@/stores/subscription'
import { logger } from '@/services/logger'

const { t } = useI18n()
const authStore = useAuthStore()
const subscriptionStore = useSubscriptionStore()

// Computed properties for template
const isAuthenticated = computed(() => authStore.isAuthenticated)
const isPremium = computed(() => subscriptionStore.isPremium)
const user = computed(() => authStore.user)

// Scroll to features section
const scrollToFeatures = () => {
  const featuresSection = document.getElementById('features')
  if (featuresSection) {
    featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// Initialize user data and subscription data for authenticated users
onMounted(async () => {
  if (isAuthenticated.value && !authStore.isTokenExpired) {
    try {
      // Refresh user data from backend to get current usage count
      await authStore.getCurrentUser()

      // Initialize subscription data
      await subscriptionStore.initializeSubscription()
    } catch (error) {
      logger.warn(
        'Failed to initialize user or subscription data:',
        { error: error instanceof Error ? error.message : String(error) },
        'home'
      )
    }
  }
})

useHead({
  title: () => t('app.title'),
  meta: [
    {
      name: 'description',
      content: () => t('app.description'),
    },
    {
      name: 'keywords',
      content: () => t('home.seo.keywords'),
    },
  ],
  script: [
    // Organization Schema - defines the brand entity for LLMs
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'n.writer',
        legalName: 'NoorWays',
        url: 'https://n-writer.app',
        logo: 'https://n-writer.app/images/noorwriter-logo.png',
        description: t('geo.definitions.noorwriter'),
        foundingDate: '2024',
        sameAs: [
          'https://chromewebstore.google.com/detail/fjlfoaokggeemlcoemkhokccampibipm',
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@noorways.fr',
          contactType: 'customer support',
          availableLanguage: ['English', 'French', 'Spanish'],
        },
      }),
    },
    // WebSite Schema with SearchAction
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'n.writer',
        url: 'https://n-writer.app',
        description: t('geo.definitions.noorwriter'),
        inLanguage: ['en', 'fr', 'es'],
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://n-writer.app/editor?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      }),
    },
    // SoftwareApplication Schema - main product definition
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'n.writer',
        description: t('geo.definitions.noorwriter'),
        applicationCategory: 'UtilitiesApplication',
        operatingSystem: 'Web, Chrome',
        url: 'https://n-writer.app',
        offers: [
          {
            '@type': 'Offer',
            name: t('pricing.plans.free.name'),
            price: '0',
            priceCurrency: 'USD',
            description: t('geo.stats.freeUsage'),
          },
          {
            '@type': 'Offer',
            name: t('pricing.plans.premium.name'),
            price: '9.99',
            priceCurrency: 'USD',
            priceValidUntil: '2025-12-31',
          },
        ],
        featureList: [
          t('home.seoFeatures.grammar.title'),
          t('home.seoFeatures.spelling.title'),
          t('home.seoFeatures.writing.title'),
          t('home.seoFeatures.plagiarism.title'),
          t('home.seoFeatures.aiDetection.title'),
          t('home.seoFeatures.citations.title'),
        ],
        author: {
          '@type': 'Organization',
          name: 'NoorWays',
        },
      }),
    },
  ],
})
</script>

<style lang="scss" scoped>
.home {
  min-height: 100vh;
}

.hero {
  background: linear-gradient(135deg, var(--nw-brand-slate, #1E293B) 0%, var(--nw-brand-slate-light, #334155) 50%, #0f172a 100%);
  color: var(--nw-text-inverse);
  padding: 4rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .hero-logo {
    margin-bottom: 2rem;
    display: flex;
    justify-content: center;
  }

  &-title {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 1rem;
    line-height: 1.1;
  }

  &-subtitle {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    opacity: 0.9;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  &-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-bottom: 2rem;
  }

  // Marketing hero for non-authenticated users
  &-marketing {
    padding: 5rem 0 6rem;
    min-height: 70vh;
    display: flex;
    align-items: center;
    
    .hero-background {
      position: absolute;
      inset: 0;
      overflow: hidden;
      pointer-events: none;
    }
    
    .hero-pattern {
      position: absolute;
      inset: 0;
      opacity: 0.1;
      background-image: radial-gradient(circle at 2px 2px, white 1px, transparent 0);
      background-size: 40px 40px;
    }
    
    .hero-orb {
      position: absolute;
      border-radius: 50%;
      filter: blur(60px);
      opacity: 0.2;
      animation: pulse 6s ease-in-out infinite;
      
      &-1 {
        width: 300px;
        height: 300px;
        background: var(--nw-primary);
        top: 10%;
        right: 5%;
        animation-delay: 0s;
      }
      
      &-2 {
        width: 350px;
        height: 350px;
        background: var(--nw-primary-light);
        bottom: 10%;
        left: 5%;
        animation-delay: 2s;
      }
      
      &-3 {
        width: 250px;
        height: 250px;
        background: var(--nw-accent);
        top: 40%;
        left: 30%;
        animation-delay: 4s;
      }
    }
    
    .hero-content {
      position: relative;
      z-index: 10;
    }
    
    .hero-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 1rem;
      background: rgba(30, 64, 175, 0.2);
      backdrop-filter: blur(4px);
      border-radius: 9999px;
      border: 1px solid rgba(30, 64, 175, 0.3);
      margin-bottom: 1.5rem;
      
      .badge-pulse {
        position: relative;
        width: 8px;
        height: 8px;
        
        &::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--nw-primary-light);
          border-radius: 50%;
          animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        &::after {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--nw-primary-light);
          border-radius: 50%;
        }
      }
      
      .badge-text {
        font-size: 0.875rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--nw-primary-light);
      }
    }
    
    .hero-title {
      font-size: clamp(2.5rem, 6vw, 4rem);
      font-weight: 800;
      margin-bottom: 1.5rem;
      line-height: 1.1;
      
      @media (min-width: 640px) {
        font-size: clamp(3rem, 6vw, 4.5rem);
      }
    }
    
    .hero-subtitle {
      font-size: 1.25rem;
      line-height: 1.6;
      color: rgba(255, 255, 255, 0.85);
      margin-bottom: 2.5rem;
      
      @media (min-width: 640px) {
        font-size: 1.375rem;
      }
    }
    
    .hero-actions {
      margin-bottom: 3rem;
      
      .btn {
        padding: 1rem 2rem;
        font-size: 1rem;
        border-radius: 1rem;
        
        &-primary {
          background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
          box-shadow: 0 8px 32px rgba(30, 64, 175, 0.4);
          
          &:hover {
            box-shadow: 0 12px 40px rgba(30, 64, 175, 0.5);
            transform: translateY(-2px) scale(1.02);
          }
        }
        
        &-secondary {
          background: transparent;
          border: 2px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(4px);
          
          &:hover {
            background: rgba(255, 255, 255, 1);
            color: var(--nw-brand-slate, #1E293B);
            border-color: transparent;
          }
        }
      }
      
      .btn-icon {
        width: 1rem;
        height: 1rem;
        margin-left: 0.5rem;
        transition: transform 0.3s ease;
      }
      
      .btn:hover .btn-icon {
        transform: translateX(4px);
      }
      
      .btn-icon-left {
        margin-left: 0;
        margin-right: 0.5rem;
      }
      
      .btn:hover .btn-icon-left {
        transform: scale(1.1);
      }
    }
    
    .hero-microcopy {
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.7);
      margin-top: -1.5rem;
      margin-bottom: 2rem;
      
      @media (min-width: 640px) {
        font-size: 0.9375rem;
      }
    }
    
    .trust-badges {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 1rem;
      padding-top: 2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      
      @media (min-width: 640px) {
        gap: 2rem;
      }
    }
    
    .trust-badge {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1.25rem;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(4px);
      border-radius: 0.75rem;
      border: 1px solid rgba(255, 255, 255, 0.2);
      font-size: 0.875rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      
      .trust-icon {
        width: 1.25rem;
        height: 1.25rem;
        color: var(--nw-primary-light);
      }
    }
  }

  // Premium user styling
  &-premium {
    background: linear-gradient(135deg, var(--nw-brand-slate, #1E293B) 0%, #065f46 50%, #064e3b 100%);
    padding: 5rem 0 6rem;
    min-height: 60vh;
    display: flex;
    align-items: center;
    
    .hero-orb-1 {
      background: var(--nw-success);
    }
    
    .hero-orb-2 {
      background: #10b981;
    }
    
    .hero-orb-3 {
      background: #34d399;
    }
    
    .premium-badge {
      background: rgba(16, 185, 129, 0.25);
      border-color: rgba(16, 185, 129, 0.4);
      margin-bottom: 2rem;
      
      .badge-pulse::before,
      .badge-pulse::after {
        background: #34d399;
      }
      
      .badge-text {
        color: #34d399;
      }
    }
    
    .trust-badges {
      gap: 0.75rem;
      
      @media (min-width: 640px) {
        gap: 1rem;
      }
    }
    
    .trust-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.875rem;
      font-size: 0.75rem;
      line-height: 1;
      
      .trust-icon {
        width: 1rem;
        height: 1rem;
        color: #34d399;
        flex-shrink: 0;
      }
      
      span {
        display: inline-flex;
        align-items: center;
      }
    }
    
    .btn-primary {
      background: linear-gradient(135deg, #10b981 0%, #059669 100%);
      box-shadow: 0 8px 32px rgba(16, 185, 129, 0.4);
      
      &:hover {
        box-shadow: 0 12px 40px rgba(16, 185, 129, 0.5);
      }
    }
  }

  // Free user styling
  &-free {
    background: linear-gradient(135deg, var(--nw-brand-slate, #1E293B) 0%, #78350f 50%, #451a03 100%);
    padding: 5rem 0 6rem;
    min-height: 60vh;
    display: flex;
    align-items: center;
    
    .hero-orb-1 {
      background: #f59e0b;
    }
    
    .hero-orb-2 {
      background: #fbbf24;
    }
    
    .hero-orb-3 {
      background: #fcd34d;
    }
    
    .free-badge {
      background: rgba(251, 191, 36, 0.25);
      border-color: rgba(251, 191, 36, 0.4);
      
      .badge-pulse::before,
      .badge-pulse::after {
        background: #fbbf24;
      }
      
      .badge-text {
        color: #fbbf24;
      }
    }
    
    .usage-card {
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 1rem;
      padding: 1.25rem 1.5rem;
      margin: 1.5rem auto 2rem;
      max-width: 320px;
      
      .usage-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.75rem;
      }
      
      .usage-label {
        font-size: 0.875rem;
        color: rgba(255, 255, 255, 0.8);
      }
      
      .usage-count {
        font-size: 1.125rem;
        font-weight: 700;
        color: #fbbf24;
      }
      
      .usage-progress {
        height: 6px;
        background: rgba(255, 255, 255, 0.2);
        border-radius: 3px;
        overflow: hidden;
        margin-bottom: 0.75rem;
      }
      
      .usage-progress-bar {
        height: 100%;
        background: linear-gradient(90deg, #fbbf24, #f59e0b);
        border-radius: 3px;
        transition: width 0.5s ease;
        
        &.usage-warning {
          background: linear-gradient(90deg, #ef4444, #dc2626);
        }
      }
      
      .upgrade-link {
        display: inline-block;
        color: #fbbf24;
        font-size: 0.875rem;
        font-weight: 600;
        text-decoration: none;
        transition: all 0.3s ease;
        
        &:hover {
          color: #fcd34d;
        }
      }
    }
    
    .trust-badges {
      gap: 0.75rem;
      
      @media (min-width: 640px) {
        gap: 1rem;
      }
    }
    
    .trust-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.875rem;
      font-size: 0.75rem;
      line-height: 1;
      
      .trust-icon {
        width: 1rem;
        height: 1rem;
        color: #fbbf24;
        flex-shrink: 0;
      }
      
      span {
        display: inline-flex;
        align-items: center;
      }
    }
    
    .btn-primary {
      background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
      box-shadow: 0 8px 32px rgba(245, 158, 11, 0.4);
      
      &:hover {
        box-shadow: 0 12px 40px rgba(245, 158, 11, 0.5);
      }
    }
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.2;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.3;
  }
}

@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.features {
  padding: 5rem 0;
  background: linear-gradient(180deg, var(--nw-bg-primary) 0%, var(--nw-bg-secondary) 50%, var(--nw-bg-primary) 100%);

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  h2 {
    text-align: center;
    font-size: 2.5rem;
    font-weight: 800;
    margin-bottom: 1rem;
    color: var(--nw-text-primary);
    
    @media (min-width: 640px) {
      font-size: 3rem;
    }
  }

  &-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    
    @media (min-width: 768px) {
      gap: 2rem;
    }
  }

  // Premium features styling
  &.premium-features {
    background: linear-gradient(135deg, var(--nw-bg-secondary) 0%, var(--nw-bg-tertiary) 100%);

    h2 {
      color: var(--nw-success);
      font-weight: 700;
    }
  }
}

.feature-card {
  background: var(--nw-bg-primary);
  padding: 2rem;
  border-radius: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--nw-border-light);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(30, 64, 175, 0.15);
    border-color: var(--nw-primary-30);
  }

  h3 {
    color: var(--nw-primary);
    margin-bottom: 1rem;
    font-size: 1.25rem;
    font-weight: 700;
  }

  p {
    color: var(--nw-text-secondary);
    line-height: 1.7;
    font-size: 0.95rem;
  }

  // Premium card styling
  &.premium-card {
    background: linear-gradient(135deg, var(--nw-bg-primary) 0%, var(--nw-bg-secondary) 100%);
    border: 2px solid var(--nw-success);
    box-shadow: 0 6px 24px rgba(34, 197, 94, 0.12);

    h3 {
      color: var(--nw-success);
      font-weight: 700;
    }

    p {
      color: var(--nw-text-secondary);
      font-weight: 500;
    }

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 12px 32px rgba(34, 197, 94, 0.2);
    }
  }
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &-primary {
    background: var(--nw-primary);
    color: var(--nw-white);

    &:hover {
      background: var(--nw-primary-light);
      transform: translateY(-2px);
    }
  }

  &-secondary {
    background: transparent;
    color: var(--nw-text-inverse);
    border: 2px solid var(--nw-text-inverse);

    &:hover {
      background: var(--nw-bg-primary);
      color: var(--nw-primary);
    }
  }

  &-large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  &-outline {
    background: transparent;
    color: var(--nw-primary);
    border: 2px solid var(--nw-primary);

    &:hover {
      background: var(--nw-primary);
      color: var(--nw-text-inverse);
    }
  }
}

.seo-features {
  padding: 6rem 0;
  background: linear-gradient(180deg, var(--nw-bg-secondary) 0%, var(--nw-bg-primary) 50%, var(--nw-bg-secondary) 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 100%;
    max-width: 1000px;
    height: 600px;
    background: radial-gradient(ellipse at center, rgba(30, 64, 175, 0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 1;
  }

  h2 {
    text-align: center;
    margin-bottom: 1rem;
    color: var(--nw-brand-slate, #1E293B);
    font-size: 2.5rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    
    @media (min-width: 640px) {
      font-size: 3rem;
    }
  }

  > p {
    text-align: center;
    margin-bottom: 4rem;
    color: var(--nw-text-secondary);
    font-size: 1.125rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.7;
  }
}

.seo-features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    gap: 2rem;
  }
  
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.seo-feature-card {
  background: var(--nw-bg-primary);
  padding: 2.5rem 2rem;
  border-radius: 1.5rem;
  box-shadow: 
    0 1px 2px rgba(0, 0, 0, 0.04),
    0 4px 16px rgba(0, 0, 0, 0.06);
  text-align: center;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid var(--nw-border-light);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--nw-primary), var(--nw-primary-light));
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    transform: translateY(-12px);
    box-shadow: 
      0 4px 8px rgba(0, 0, 0, 0.04),
      0 24px 48px rgba(30, 64, 175, 0.15);
    border-color: rgba(30, 64, 175, 0.2);
    
    &::before {
      transform: scaleX(1);
    }
  }

  .feature-icon-wrapper {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 4.5rem;
    height: 4.5rem;
    margin-bottom: 1.5rem;
  }
  
  .feature-icon-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--nw-primary) 0%, var(--nw-primary-dark) 100%);
    border-radius: 1.25rem;
    transform: rotate(0deg);
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    
    &::after {
      content: '';
      position: absolute;
      inset: -2px;
      background: linear-gradient(135deg, rgba(30, 64, 175, 0.4), rgba(30, 58, 138, 0.2));
      border-radius: 1.375rem;
      z-index: -1;
      opacity: 0;
      transition: opacity 0.5s ease;
    }
  }
  
  &:hover .feature-icon-bg {
    transform: rotate(6deg) scale(1.05);
    
    &::after {
      opacity: 1;
    }
  }
  
  .feature-icon-svg {
    position: relative;
    z-index: 1;
    width: 2rem;
    height: 2rem;
    color: white;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover .feature-icon-svg {
    transform: scale(1.1);
  }

  h3 {
    margin-bottom: 0.875rem;
    color: var(--nw-brand-slate, #1E293B);
    font-size: 1.25rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }

  p {
    margin-bottom: 1.5rem;
    color: var(--nw-text-secondary);
    line-height: 1.7;
    font-size: 0.9375rem;
  }

  .feature-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--nw-primary);
    text-decoration: none;
    font-weight: 600;
    font-size: 0.9375rem;
    padding: 0.5rem 1rem;
    border-radius: 0.75rem;
    background: rgba(30, 64, 175, 0.08);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    .link-arrow {
      width: 1rem;
      height: 1rem;
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    &:hover {
      background: var(--nw-primary);
      color: white;
      gap: 0.75rem;
      
      .link-arrow {
        transform: translateX(4px);
      }
    }
  }
}

.cta-section {
  background: linear-gradient(135deg, var(--nw-brand-slate, #1E293B) 0%, var(--nw-primary) 50%, var(--nw-primary-dark) 100%);
  padding: 5rem 0;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle at 2px 2px, rgba(255, 255, 255, 0.1) 1px, transparent 0);
    background-size: 32px 32px;
    pointer-events: none;
  }

  .container {
    max-width: 800px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 10;
  }

  h2 {
    font-size: 2.5rem;
    font-weight: 800;
    color: var(--nw-white);
    margin-bottom: 1rem;
    
    @media (min-width: 640px) {
      font-size: 3rem;
    }
  }

  p {
    font-size: 1.25rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 2.5rem;
    line-height: 1.6;
  }
}

.cta-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  
  .btn {
    padding: 1rem 2rem;
    border-radius: 1rem;
    font-size: 1rem;
    
    &-primary {
      background: var(--nw-white);
      color: var(--nw-brand-slate, #1E293B);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
      
      &:hover {
        transform: translateY(-2px) scale(1.02);
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
      }
    }
    
    &-outline {
      background: transparent;
      color: var(--nw-white);
      border: 2px solid rgba(255, 255, 255, 0.5);
      
      &:hover {
        background: var(--nw-white);
        color: var(--nw-brand-slate, #1E293B);
        border-color: transparent;
      }
    }
  }
}
</style>

<!-- Dark theme overrides - unscoped to properly target html.dark -->
<style lang="scss">
html.dark {
  .home {
    // Hero sections - ensure text is always white/light on dark gradient backgrounds
    .hero {
      color: #F1F5F9;

      .hero-title {
        color: #FFFFFF;
      }

      .hero-subtitle {
        color: rgba(255, 255, 255, 0.85);
      }

      .hero-badge {
        .badge-text {
          color: #3B82F6;
        }
      }

      .trust-badge {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.9);

        .trust-icon {
          color: #3B82F6;
        }
      }

      .btn-secondary {
        color: #FFFFFF;
        border-color: rgba(255, 255, 255, 0.3);

        &:hover {
          background: #FFFFFF;
          color: #1E293B;
        }
      }
    }

    .hero-premium {
      .premium-badge .badge-text {
        color: #34d399;
      }

      .trust-badge .trust-icon {
        color: #34d399;
      }
    }

    .hero-free {
      .free-badge .badge-text {
        color: #fbbf24;
      }

      .trust-badge .trust-icon {
        color: #fbbf24;
      }

      .usage-card {
        .usage-label {
          color: rgba(255, 255, 255, 0.8);
        }

        .usage-count {
          color: #fbbf24;
        }

        .upgrade-link {
          color: #fbbf24;

          &:hover {
            color: #fcd34d;
          }
        }
      }
    }

    .features {
      background: linear-gradient(180deg, #0F172A 0%, #1E293B 50%, #0F172A 100%);

      h2 {
        color: #F1F5F9;
      }

      &.premium-features {
        background: linear-gradient(135deg, #1E293B 0%, #0F172A 100%);

        h2 {
          color: #6EE7B7;
        }
      }
    }

    .feature-card {
      background: #1E293B;
      border-color: #334155;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

      &:hover {
        box-shadow: 0 12px 40px rgba(30, 64, 175, 0.2);
        border-color: rgba(30, 64, 175, 0.3);
      }

      h3 {
        color: #3B82F6;
      }

      p {
        color: #CBD5E1;
      }

      &.premium-card {
        background: linear-gradient(135deg, #1E293B 0%, #0F172A 100%);
        border-color: #10B981;

        h3 {
          color: #6EE7B7;
        }

        p {
          color: #CBD5E1;
        }

        &:hover {
          box-shadow: 0 12px 32px rgba(16, 185, 129, 0.25);
        }
      }
    }

    .seo-features {
      background: linear-gradient(180deg, #1E293B 0%, #0F172A 50%, #1E293B 100%);
      
      &::before {
        background: radial-gradient(ellipse at center, rgba(30, 64, 175, 0.12) 0%, transparent 70%);
      }

      h2 {
        color: #F1F5F9;
      }

      > p {
        color: #94A3B8;
      }
    }

    .seo-feature-card {
      background: rgba(15, 23, 42, 0.8);
      border-color: #334155;
      box-shadow: 
        0 1px 2px rgba(0, 0, 0, 0.1),
        0 4px 16px rgba(0, 0, 0, 0.2);
      
      &::before {
        background: linear-gradient(90deg, #1E40AF, #3B82F6);
      }

      &:hover {
        box-shadow: 
          0 4px 8px rgba(0, 0, 0, 0.1),
          0 24px 48px rgba(30, 64, 175, 0.2);
        border-color: rgba(59, 130, 246, 0.3);
      }
      
      .feature-icon-bg {
        background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
        
        &::after {
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.3), rgba(30, 64, 175, 0.15));
        }
      }

      h3 {
        color: #F1F5F9;
      }

      p {
        color: #94A3B8;
      }

      .feature-link {
        color: #3B82F6;
        background: rgba(59, 130, 246, 0.1);

        &:hover {
          background: #1E40AF;
          color: #0F172A;
        }
      }
    }
  }
}
</style>
